/**
 * Settlement panel — modal DOM overlay for build + recruit orders at a
 * player-owned city/province (v0M3.10, revised v0M3.5+fix).
 *
 * Rebuild discipline: DOM structure is built ONCE per (provinceId, tab)
 * change. Per-frame `sync()` only mutates existing element text/disabled
 * states — never `innerHTML = ''`. This preserves click handlers so
 * buttons actually fire.
 */

import { injectMenuStyles } from './menu-styles';
import { loadCohorts } from '../../data/cohorts';
import { loadStructures } from '../../data/structures';
import { loadSpecialUnits } from '../../data/special-units';
import { loadDials, getDialNumber } from '../../data/dials';
import { loadKnowledge } from '../../data/knowledge';
import { garrisonMaxBonus } from '../../sim/structure-effects';
import type { Command } from '../../sim/commands';
import type {
  ArmyId,
  Settlement,
  FactionId,
  GameState,
  Province,
  ProvinceId,
  SettlementTier,
} from '../../sim/state';
import { sortedIds, toArmyId, TICKS_PER_GAME_DAY, SECONDS_PER_TICK } from '../../sim/state';

// v0M4.2.1 — Garrison cap per city (Balance.xlsx dial per BATTLES.md §3.2).
const CITY_PANEL_DIALS = loadDials();
const GARRISON_MAX_PER_SETTLEMENT = Math.max(0, Math.floor(getDialNumber(CITY_PANEL_DIALS, 'garrison_max_per_settlement')));
const GARRISON_COHORT_TYPE = 'Garrison';
// MFix7.2 (2026-07-23) — cap fraction for the manpower display in the header.
const MANPOWER_CAP_FRACTION_FOR_HEADER = getDialNumber(CITY_PANEL_DIALS, 'manpower_cap_fraction_of_pop');

const STRUCTURES = loadStructures();
const COHORTS = loadCohorts();
// v0M5.3 (2026-07-25) — SpecialUnits roster (Spy at v1). Used for the
// Special units section at the bottom of the Recruit tab.
const SPECIAL_UNITS = loadSpecialUnits();
// v0M9.11 (2026-07-24) — Knowledge loaded once for the "Unlocks Structure"
// gate. Build order below now auto-derives from the full structure roster
// so Michael's xlsx additions surface without a code change.
const KNOWLEDGE = loadKnowledge();

/**
 * v0M9.11 (2026-07-24) — Build order now derives from Object.keys of
 * the full structures dict. Order = xlsx insertion order (Michael can
 * reorder by rearranging rows). Retires the hardcoded 6-structure list.
 *
 * A curated preferred-order head is applied first so the original 6
 * structures always lead the list even if xlsx column order shifts —
 * defensive against Michael accidentally moving a row.
 */
const PREFERRED_BUILD_ORDER_HEAD: readonly string[] = [
  'Blacksmith', 'Forester', 'Farm', 'Monastery', 'Stables', 'Market',
];
const BUILD_ORDER: readonly string[] = (() => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const name of PREFERRED_BUILD_ORDER_HEAD) {
    if (STRUCTURES[name]) { out.push(name); seen.add(name); }
  }
  for (const name of Object.keys(STRUCTURES)) {
    if (!seen.has(name)) out.push(name);
  }
  return out;
})();

/**
 * v0M9.12 (2026-07-24) — Build a static map from structure name to the
 * knowledge that unlocks it. Any knowledge with an `Unlocks Structure: X`
 * effect string registers X here. Consumed by the per-frame refresh
 * loop to decide whether to lock a build row.
 *
 * Static because knowledge and structure lists are xlsx-authored + loaded
 * once at module init. If Michael later changes the effect phrasing, the
 * regex here must match.
 */
const UNLOCKS_STRUCTURE_RE = /Unlocks\s+Structure:\s*(.+)/i;
const STRUCTURE_UNLOCK_KNOWLEDGE: Map<string, string> = (() => {
  const map = new Map<string, string>();
  for (const knowledgeName of Object.keys(KNOWLEDGE)) {
    const spec = KNOWLEDGE[knowledgeName]!;
    const m = UNLOCKS_STRUCTURE_RE.exec(spec.effects);
    if (m && m[1]) {
      map.set(m[1].trim(), knowledgeName);
    }
  }
  return map;
})();
const RECRUIT_ORDER: readonly string[] = [
  'Swordsman',
  'Archer',
  'Light Cavalry',
  'Spearman',
  'Shock Cavalry',
  'Catapult',
];

const STRUCTURE_CAP_BY_TIER: Record<SettlementTier, number> = {
  Village: 5,
  Town: 10,
  City: 20,
};

export interface CityPanelCallbacks {
  onIssue(cmd: Command): void;
  onClose(): void;
}

export interface CityPanelHandle {
  sync(state: GameState, openProvinceId: ProvinceId | null, playerFactionId: FactionId | null): void;
  destroy(): void;
}

interface BuildRow {
  container: HTMLElement;
  button: HTMLButtonElement;
  /** MFix10 (2026-07-24) — small "N×" badge shown left of the name so the
   *  player can see at-a-glance how many of this structure the settlement
   *  currently owns. Updated in refresh() from province.structures. */
  countBadge: HTMLElement;
  name: string;
}

/**
 * MFix10 (2026-07-24) — one row per active build-queue entry. Mirrors
 * RecruitQueueRow shape so the two queue panels look and update identically.
 */
interface BuildQueueRow {
  container: HTMLElement;
  label: HTMLElement;
  progressFill: HTMLElement;
  structureName: string;
  buildTimeSec: number;
}

interface RecruitRow {
  container: HTMLElement;
  button: HTMLButtonElement;
  name: string;
  spec: (typeof COHORTS)[string];
  /** MFix8.5 — collapsed stats block (Figures / Health / Attack / … / Training). */
  statsBlock: HTMLElement;
}

// MFix8.5 (2026-07-24) — localStorage key for the "Show cohort stats" toggle.
const RECRUIT_STATS_LS_KEY = 'su-recruit-show-stats';
function loadRecruitStatsPref(): boolean {
  try { return localStorage.getItem(RECRUIT_STATS_LS_KEY) === '1'; }
  catch { return false; }
}
function saveRecruitStatsPref(on: boolean): void {
  try { localStorage.setItem(RECRUIT_STATS_LS_KEY, on ? '1' : '0'); }
  catch { /* noop */ }
}

/**
 * MFix8.6 (2026-07-24) — map cohort type name → icon filename in
 * `/public/icons/units/`. Kebab-case per file convention. Garrison
 * currently reuses spearman.svg pending Michael sourcing a dedicated
 * garrison silhouette; flag when missing.
 */
function recruitIconUrl(cohortType: string): string {
  const map: Record<string, string> = {
    'Swordsman': '/icons/units/swordsman.svg',
    'Archer': '/icons/units/archer.svg',
    'Light Cavalry': '/icons/units/light-cavalry.svg',
    'Spearman': '/icons/units/spearman.svg',
    'Shock Cavalry': '/icons/units/shock-cavalry.svg',
    'Catapult': '/icons/units/catapult.svg',
    'Garrison': '/icons/units/garrison.svg',
  };
  return map[cohortType] ?? '/icons/units/swordsman.svg';
}

// MFix1.3 (2026-07-16) — one row per pending recruit-queue entry.
interface RecruitQueueRow {
  container: HTMLElement;
  label: HTMLElement;
  progressFill: HTMLElement;
  // Display label — cohort type name for basic recruits, special-unit
  // name (e.g. 'Spy') for v0M5.4d special-unit recruits.
  displayName: string;
  recruitTimeSec: number;
  // v0M5.4d (2026-07-25) — flag to route the target label away from
  // "Army #N" when this row is a special-unit recruit.
  isSpecialUnit: boolean;
}

/**
 * v0M4.2.1 — find the Settlement entity that lives in the given province, or
 * null if none exists. At v1 only capital cities are materialized as
 * `state.settlements` entries (per `scenario.ts` assignCapitals); minor
 * cities from the map data are not yet Settlement entities. Consumers of the
 * garrison UI use this to gate the "Recruit Garrison" button.
 *
 * Iteration is stable per SG determinism discipline — the return picks
 * the lowest-id Settlement if a province ever gets multiple (defensive; not
 * expected at v0M4.2.1).
 */
function findCityInProvince(state: GameState, provinceId: ProvinceId): Settlement | null {
  let best: Settlement | null = null;
  for (const settlementId of sortedIds(state.settlements)) {
    const c = state.settlements.get(settlementId);
    if (!c) continue;
    if (c.provinceId === provinceId) {
      if (best === null || (c.id as unknown as number) < (best.id as unknown as number)) {
        best = c;
      }
    }
  }
  return best;
}

export function mountSettlementPanel(root: HTMLElement, callbacks: CityPanelCallbacks): CityPanelHandle {
  injectMenuStyles();

  const backdrop = document.createElement('div');
  backdrop.className = 'su-city-panel-backdrop';
  backdrop.style.display = 'none';

  const panel = document.createElement('div');
  panel.className = 'su-city-panel';
  backdrop.appendChild(panel);

  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) callbacks.onClose();
  });

  // Persistent DOM structure — built ONCE. Header title mutates textContent.
  const header = document.createElement('div');
  header.className = 'su-city-panel-header';
  const title = document.createElement('h2');
  title.className = 'su-city-panel-title';
  header.appendChild(title);
  const closeBtn = document.createElement('button');
  closeBtn.className = 'su-city-panel-close';
  closeBtn.textContent = '×';
  closeBtn.title = 'Close (Esc)';
  closeBtn.addEventListener('click', () => callbacks.onClose());
  header.appendChild(closeBtn);
  panel.appendChild(header);

  const tabs = document.createElement('div');
  tabs.className = 'su-city-panel-tabs';
  const buildTabBtn = document.createElement('button');
  buildTabBtn.className = 'su-city-panel-tab su-active';
  buildTabBtn.textContent = 'Build';
  const recruitTabBtn = document.createElement('button');
  recruitTabBtn.className = 'su-city-panel-tab';
  recruitTabBtn.textContent = 'Recruit';
  tabs.appendChild(buildTabBtn);
  tabs.appendChild(recruitTabBtn);
  panel.appendChild(tabs);

  const body = document.createElement('div');
  body.className = 'su-city-panel-body';
  panel.appendChild(body);

  // Sub-containers — one per tab. We hide/show rather than rebuild.
  const buildContainer = document.createElement('div');
  const recruitContainer = document.createElement('div');
  body.appendChild(buildContainer);
  body.appendChild(recruitContainer);

  // Local UI state
  let currentTab: 'build' | 'recruit' = 'build';
  let renderedProvinceId: number | null = null;
  let buildRows: BuildRow[] = [];
  let recruitRows: RecruitRow[] = [];
  let armySelect: HTMLSelectElement | null = null;
  let recruitVillageNote: HTMLElement | null = null;
  let buildCapNote: HTMLElement | null = null;
  // MFix10 (2026-07-24) — Build tab "In Progress" section (parallel to the
  // Recruit tab's queue viz). Header + list + row cache + structure hash
  // for the rebuild-only-on-shape-change pattern.
  let buildQueueHeader: HTMLElement | null = null;
  let buildQueueList: HTMLElement | null = null;
  let buildQueueRows: BuildQueueRow[] = [];
  let lastBuildQueueStructureHash = '';
  // v0M4.2.1 — garrison sub-section (per BATTLES.md §3.2). Sits at the
  // top of the Recruit tab, above the army-target selector.
  let garrisonSection: HTMLElement | null = null;
  let garrisonCountLabel: HTMLElement | null = null;
  let garrisonRecruitBtn: HTMLButtonElement | null = null;
  let garrisonNoCityNote: HTMLElement | null = null;
  // v0M5.3 (2026-07-25) — Special-unit recruit buttons (Spy at v1).
  // Rebuilt per structural refresh; refresh() enables/disables based on
  // gold + structure prereq.
  const specialRecruitButtons: HTMLButtonElement[] = [];
  // MFix1.3 — recruitment queue visualization.
  let recruitQueueHeader: HTMLElement | null = null;
  let recruitQueueList: HTMLElement | null = null;
  let recruitQueueRows: RecruitQueueRow[] = [];
  let lastRecruitQueueStructureHash = '';
  // v0M3.5+fix4 — hash of the player-army-id list from the last refresh.
  // We only rebuild armySelect options when this changes; otherwise the
  // dropdown gets clobbered every frame and clicks land on stale/empty options.
  let lastArmyListHash = '';

  buildTabBtn.addEventListener('click', () => {
    currentTab = 'build';
    applyTab();
  });
  recruitTabBtn.addEventListener('click', () => {
    currentTab = 'recruit';
    applyTab();
  });

  function applyTab(): void {
    buildTabBtn.className = currentTab === 'build' ? 'su-city-panel-tab su-active' : 'su-city-panel-tab';
    recruitTabBtn.className = currentTab === 'recruit' ? 'su-city-panel-tab su-active' : 'su-city-panel-tab';
    buildContainer.style.display = currentTab === 'build' ? '' : 'none';
    recruitContainer.style.display = currentTab === 'recruit' ? '' : 'none';
  }

  root.appendChild(backdrop);

  // First-time structural build for a given province.
  function structuralBuild(state: GameState, province: Province, playerFactionId: FactionId): void {
    // Clear old rows.
    buildContainer.innerHTML = '';
    recruitContainer.innerHTML = '';
    buildRows = [];
    recruitRows = [];
    armySelect = null;

    // MFix8.3 (2026-07-24) — always-visible "Structures: N / cap" header on
    // the Build tab. Reuses the buildCapNote element (renamed conceptually
    // to buildStatusNote in prose but the variable is unchanged for diff
    // hygiene). Turns red when at cap.
    buildCapNote = document.createElement('div');
    buildCapNote.className = 'su-city-panel-note';
    buildCapNote.style.fontWeight = 'bold';
    buildCapNote.style.marginBottom = '8px';
    buildContainer.appendChild(buildCapNote);

    // MFix10 (2026-07-24) — Build tab "In Progress" section. Mirrors the
    // Recruit tab pattern (header + list of animated bars). Rebuild the
    // rows only when the queue *shape* changes; otherwise mutate fill
    // widths in place so the bar animates smoothly per frame.
    buildQueueHeader = document.createElement('div');
    buildQueueHeader.className = 'su-city-panel-note';
    buildQueueHeader.style.marginTop = '4px';
    buildQueueHeader.style.fontWeight = 'bold';
    buildQueueHeader.textContent = 'In Progress';
    buildQueueHeader.style.display = 'none';
    buildContainer.appendChild(buildQueueHeader);
    buildQueueList = document.createElement('div');
    buildQueueList.style.marginBottom = '8px';
    buildContainer.appendChild(buildQueueList);
    buildQueueRows = [];
    lastBuildQueueStructureHash = '';

    for (const name of BUILD_ORDER) {
      const spec = STRUCTURES[name];
      if (!spec) continue;
      const row = document.createElement('div');
      row.className = 'su-city-panel-row';
      // MFix10 (2026-07-24) — count badge left of the name so the player
      // sees at-a-glance how many of this structure are already built.
      // Fixed-width so names align across rows. Dim when 0, bright when >0.
      const countBadge = document.createElement('div');
      countBadge.textContent = '0×';
      countBadge.style.minWidth = '28px';
      countBadge.style.textAlign = 'right';
      countBadge.style.marginRight = '8px';
      countBadge.style.fontSize = '12px';
      countBadge.style.opacity = '0.5';
      countBadge.style.flexShrink = '0';
      row.appendChild(countBadge);
      // MFix12.3 (2026-07-27) — wrap name in a flex column so a
      // description subtext can sit below it. Retains the existing
      // horizontal row layout (badge | name+desc | cost | button).
      const nameCol = document.createElement('div');
      nameCol.style.display = 'flex';
      nameCol.style.flexDirection = 'column';
      nameCol.style.flex = '1';
      nameCol.style.minWidth = '0';
      const nm = document.createElement('div');
      nm.className = 'su-city-panel-item-name';
      // Keep the produces/rate suffix only when the structure actually
      // produces something — non-producing rows are just the name.
      nm.textContent = spec.ratePerSec > 0 && spec.produces !== ''
        ? `${name} → ${spec.produces} +${spec.ratePerSec}/s`
        : name;
      nameCol.appendChild(nm);
      // MFix12.3 — player-facing description subtext (falls back to
      // designer `effects` if xlsx didn't populate a description yet, and
      // hides entirely if both are empty). Small italic muted; wraps.
      const descText = (spec.description ?? '').trim() || (spec.effects ?? '').trim();
      if (descText) {
        const desc = document.createElement('div');
        desc.textContent = descText;
        desc.style.fontSize = '10px';
        desc.style.opacity = '0.65';
        desc.style.fontStyle = 'italic';
        desc.style.marginTop = '2px';
        desc.style.lineHeight = '1.3';
        desc.style.whiteSpace = 'normal';
        nameCol.appendChild(desc);
      }
      row.appendChild(nameCol);
      const cost = document.createElement('div');
      cost.className = 'su-city-panel-item-cost';
      cost.textContent = `${spec.goldCost} Gold, ${spec.buildTimeSec}s`;
      row.appendChild(cost);
      const btn = document.createElement('button');
      btn.className = 'su-city-panel-action';
      btn.textContent = 'Build';
      btn.addEventListener('click', () => {
        callbacks.onIssue({
          type: 'build_structure',
          tick: 0, // client tick unused server-side; apply-commands stamps from state
          playerFactionId,
          provinceId: province.id,
          structureName: name,
        });
      });
      row.appendChild(btn);
      buildContainer.appendChild(row);
      buildRows.push({ container: row, button: btn, countBadge, name });
    }

    // Recruit tab structure.
    recruitVillageNote = document.createElement('div');
    recruitVillageNote.className = 'su-city-panel-note';
    recruitVillageNote.textContent = 'Villages cannot recruit cohorts. Upgrade to Town+.';
    recruitVillageNote.style.display = 'none';
    recruitContainer.appendChild(recruitVillageNote);

    // MFix8.5 (2026-07-24) — "Show cohort stats" toggle. Off by default;
    // when on, expands each recruit row with Figures / Health / Attack /
    // Defence / Armor / speeds / build cost / training time. Preference
    // persists in localStorage (per-browser, cross-session).
    const statsToggleWrap = document.createElement('label');
    statsToggleWrap.style.display = 'flex';
    statsToggleWrap.style.alignItems = 'center';
    statsToggleWrap.style.gap = '6px';
    statsToggleWrap.style.margin = '4px 0 8px 0';
    statsToggleWrap.style.fontSize = '11px';
    statsToggleWrap.style.opacity = '0.85';
    statsToggleWrap.style.cursor = 'pointer';
    const statsToggle = document.createElement('input');
    statsToggle.type = 'checkbox';
    statsToggle.checked = loadRecruitStatsPref();
    statsToggle.addEventListener('change', () => {
      saveRecruitStatsPref(statsToggle.checked);
      for (const r of recruitRows) {
        r.statsBlock.style.display = statsToggle.checked ? '' : 'none';
      }
    });
    statsToggleWrap.appendChild(statsToggle);
    const statsToggleLabel = document.createElement('span');
    statsToggleLabel.textContent = 'Show cohort stats';
    statsToggleWrap.appendChild(statsToggleLabel);
    recruitContainer.appendChild(statsToggleWrap);

    // v0M4.2.1 — Garrison section (BATTLES.md §3.2). Recruits city-owned
    // cohorts that defend during siege battles. Independent of the
    // army-target selector below — garrisons don't attach to armies.
    garrisonSection = document.createElement('div');
    garrisonSection.className = 'su-city-panel-row';
    garrisonSection.style.marginBottom = '8px';
    const garrisonName = document.createElement('div');
    garrisonName.className = 'su-city-panel-item-name';
    garrisonName.textContent = 'Garrison';
    garrisonSection.appendChild(garrisonName);
    garrisonCountLabel = document.createElement('div');
    garrisonCountLabel.className = 'su-city-panel-item-cost';
    garrisonCountLabel.textContent = `0 / ${GARRISON_MAX_PER_SETTLEMENT}`;
    garrisonSection.appendChild(garrisonCountLabel);
    garrisonRecruitBtn = document.createElement('button');
    garrisonRecruitBtn.className = 'su-city-panel-action';
    garrisonRecruitBtn.textContent = 'Recruit';
    garrisonRecruitBtn.addEventListener('click', () => {
      // Find the Settlement entity that lives in this province (v1: capital only —
      // minor cities are not materialized as Settlement entities yet). If none,
      // the button is disabled below in refresh(); this handler still
      // guards defensively.
      const city = findCityInProvince(state, province.id);
      if (!city) return;
      callbacks.onIssue({
        type: 'recruit_cohort',
        tick: 0,
        playerFactionId,
        provinceId: province.id,
        cohortType: GARRISON_COHORT_TYPE,
        armyId: null,
        settlementId: city.id,
      });
    });
    garrisonSection.appendChild(garrisonRecruitBtn);
    recruitContainer.appendChild(garrisonSection);

    garrisonNoCityNote = document.createElement('div');
    garrisonNoCityNote.className = 'su-city-panel-note';
    garrisonNoCityNote.style.fontSize = '11px';
    garrisonNoCityNote.style.opacity = '0.7';
    garrisonNoCityNote.style.display = 'none';
    garrisonNoCityNote.textContent = 'No garrison-eligible city in this province.';
    recruitContainer.appendChild(garrisonNoCityNote);

    // Army-target selector.
    const targetNote = document.createElement('div');
    targetNote.className = 'su-city-panel-note';
    targetNote.style.display = 'flex';
    targetNote.style.alignItems = 'center';
    targetNote.style.gap = '10px';
    targetNote.appendChild(document.createTextNode('Recruit into:'));
    armySelect = document.createElement('select');
    armySelect.className = 'su-city-panel-army-select';
    targetNote.appendChild(armySelect);
    recruitContainer.appendChild(targetNote);

    // MFix1.3 — persistent "In Progress" section above the recruit
     // options list. Header + list container; rows populate in refresh().
    recruitQueueHeader = document.createElement('div');
    recruitQueueHeader.className = 'su-city-panel-note';
    recruitQueueHeader.style.marginTop = '10px';
    recruitQueueHeader.style.fontWeight = 'bold';
    recruitQueueHeader.textContent = 'In Progress';
    recruitQueueHeader.style.display = 'none';
    recruitContainer.appendChild(recruitQueueHeader);
    recruitQueueList = document.createElement('div');
    recruitQueueList.style.marginBottom = '8px';
    recruitContainer.appendChild(recruitQueueList);
    recruitQueueRows = [];
    lastRecruitQueueStructureHash = '';

    for (const name of RECRUIT_ORDER) {
      const spec = COHORTS[name];
      if (!spec) continue;
      // Outer container = the row + its optional stats block, so hiding
      // stats doesn't disturb neighbouring rows.
      const outer = document.createElement('div');
      outer.style.marginBottom = '4px';
      const row = document.createElement('div');
      row.className = 'su-city-panel-row';
      // MFix8.6 (2026-07-24) — icon per cohort type. 24×24 in the row
      // header. Uses the sourced Game-Icons.net units set.
      // MFix8 (2026-07-25): icon SVGs use fill="#fff" (verified via network
      // fetch), so drawing them onto the dark row bg (rgb(26,20,10)) is
      // legible as-is. Removed the stale `invert(0.9)` filter which was
      // inverting white → near-black and making the icons invisible.
      const icon = document.createElement('img');
      icon.src = recruitIconUrl(name);
      icon.alt = name;
      icon.style.width = '24px';
      icon.style.height = '24px';
      icon.style.flexShrink = '0';
      icon.style.marginRight = '6px';
      row.appendChild(icon);
      const nm = document.createElement('div');
      nm.className = 'su-city-panel-item-name';
      nm.textContent = name;
      row.appendChild(nm);
      const cost = document.createElement('div');
      cost.className = 'su-city-panel-item-cost';
      const costBits: string[] = [];
      if (spec.goldCost > 0) costBits.push(`${spec.goldCost}G`);
      if (spec.ironCost > 0) costBits.push(`${spec.ironCost}I`);
      if (spec.woodCost > 0) costBits.push(`${spec.woodCost}W`);
      if (spec.horsesCost > 0) costBits.push(`${spec.horsesCost}H`);
      if (spec.suppliesCost > 0) costBits.push(`${spec.suppliesCost}F`);
      // MFix6.6.6 (2026-07-23): cohorts now cost 1 manpower per figure
      // (spec.figureCount). Population cost retained as info string only
      // for consistency with old xlsx sheets; economy no longer touches
      // population on recruit.
      costBits.push(`${spec.figureCount}mp`);
      cost.textContent = costBits.join(' ') + ` — ${spec.recruitTimeSec}s`;
      row.appendChild(cost);
      const btn = document.createElement('button');
      btn.className = 'su-city-panel-action';
      btn.textContent = 'Recruit';
      btn.addEventListener('click', () => {
        const target = armySelect && armySelect.value !== '' ? toArmyId(Number(armySelect.value)) : null;
        callbacks.onIssue({
          type: 'recruit_cohort',
          tick: 0,
          playerFactionId,
          provinceId: province.id,
          cohortType: name,
          armyId: target,
          // v0M4.2.1: army-recruit path always sends settlementId: null.
          // Garrison recruits use the dedicated button above with settlementId set.
          settlementId: null,
        });
      });
      row.appendChild(btn);
      outer.appendChild(row);

      // MFix8.5 (2026-07-24) — expandable stats block. Hidden by default;
      // toggle via the "Show cohort stats" checkbox above. Population +
      // combat + logistics stats consulted from the CohortSpec.
      const statsBlock = document.createElement('div');
      statsBlock.style.fontSize = '10px';
      statsBlock.style.opacity = '0.75';
      statsBlock.style.padding = '4px 8px 6px 30px'; // indent past the icon
      statsBlock.style.lineHeight = '1.5';
      statsBlock.style.display = statsToggle.checked ? '' : 'none';
      // Two-column layout: attribute name : value, comma-separated pairs
      // on each visual line for compactness.
      const speedCampaign = spec.moveSpeedCampaign?.toFixed(2) ?? '—';
      const speedCombat = spec.moveSpeedCombat ?? '—';
      const health = spec.maxHealthPerFigure;
      const front = spec.frontWidth ?? 12;
      statsBlock.innerHTML =
        `<div>Figures: ${spec.figureCount} (${front} wide) &nbsp; Health/figure: ${health}</div>` +
        `<div>Attack: ${spec.attack} &nbsp; Defence: ${spec.defence} &nbsp; Armor: ${spec.armor} &nbsp; Init: ${spec.initiative}</div>` +
        `<div>Missile: ${spec.missileAttack}${spec.missileRange > 0 ? ` (range ${spec.missileRange})` : ''} &nbsp; Charge: +${spec.chargeBonus}</div>` +
        `<div>Speed — campaign: ${speedCampaign} c/tick, combat: ${speedCombat} sq/round</div>` +
        `<div>Build cost: ${costBits.join(', ')} &nbsp; Training: ${spec.recruitTimeSec}s</div>` +
        (spec.tags.length > 0 ? `<div>Tags: ${spec.tags.join(', ')}</div>` : '');
      outer.appendChild(statsBlock);

      recruitContainer.appendChild(outer);
      recruitRows.push({ container: outer, button: btn, name, spec, statsBlock });
    }

    // v0M5.3 (2026-07-25) — SpecialUnits section (Spy at v1). Renders
    // after the cohort rows in the Recruit tab. Each row shows the
    // special unit name + cost; button dispatches `recruit_spy` which
    // spawns the Spy at the settlement cell (MVP: immediate spawn,
    // no queue). Gated by faction gold ≥ goldCost + settlement's
    // province has the structure_prereq (Thieves Guild for Spy).
    const specialSection = document.createElement('div');
    specialSection.style.marginTop = '10px';
    specialSection.style.paddingTop = '8px';
    specialSection.style.borderTop = '1px solid #4a3820';
    const specialLabel = document.createElement('div');
    specialLabel.style.fontSize = '12px';
    specialLabel.style.opacity = '0.85';
    specialLabel.style.marginBottom = '6px';
    specialLabel.textContent = 'Special units';
    specialSection.appendChild(specialLabel);
    for (const specialName of Object.keys(SPECIAL_UNITS)) {
      const spec = SPECIAL_UNITS[specialName];
      if (!spec) continue;
      const row = document.createElement('div');
      row.className = 'su-city-panel-row';
      const nm = document.createElement('div');
      nm.className = 'su-city-panel-item-name';
      nm.textContent = specialName;
      row.appendChild(nm);
      const cost = document.createElement('div');
      cost.className = 'su-city-panel-item-cost';
      const costBits: string[] = [];
      if (spec.goldCost > 0) costBits.push(`${spec.goldCost}G`);
      const gate = spec.structurePrereq ? ` — needs ${spec.structurePrereq}` : '';
      cost.textContent = costBits.join(' ') + gate;
      row.appendChild(cost);
      const btn = document.createElement('button');
      btn.className = 'su-city-panel-action';
      btn.textContent = 'Recruit';
      btn.dataset.specialUnitName = specialName;
      btn.addEventListener('click', () => {
        // Recruit the special unit at the province's capital settlement.
        // findCityInProvince returns the (first) major-city Settlement for
        // this province; MVP dispatches recruit_spy against it. Immediate
        // spawn at the cell — no queue at v0M5.3. Uses the stale `state`
        // captured at build time only to resolve settlement id; the tick
        // field is a placeholder like every other command in this file
        // (the sim ignores it for recruit-family commands).
        const city = findCityInProvince(state, province.id);
        if (!city) return;
        callbacks.onIssue({
          type: 'recruit_spy',
          tick: 0,
          playerFactionId,
          settlementId: city.id,
          specialUnitType: specialName,
        });
      });
      row.appendChild(btn);
      specialRecruitButtons.push(btn);
      specialSection.appendChild(row);
    }
    recruitContainer.appendChild(specialSection);

    applyTab();
    // Silence unused-var warnings on `state` — used only via callbacks below.
    void state;
  }

  // Per-frame update — only touches existing elements.
  function refresh(state: GameState, province: Province, playerFactionId: FactionId): void {
    const faction = state.factions.get(playerFactionId);
    if (!faction) return;

    // Header.
    // MFix2.4 — round population to integer (sim tracks fractional pop
    // from per-tick growth math; displaying "9847.3" is noise).
    // MFix7.2 (2026-07-23) — append manpower (current / cap) drawn from
    // the recruiting city. Cap = pop × manpower_cap_fraction_of_pop.
    // MFix16.1 (2026-07-29) — show 1-decimal precision when the pool
    // is below 10; at low generation rates the pool accumulates in
    // sub-integer steps and player needs to see the progress.
    const cityForHeader = findCityInProvince(state, province.id);
    const mpRaw = cityForHeader?.manpower ?? 0;
    const mpDisplay = mpRaw < 10 ? mpRaw.toFixed(1) : Math.floor(mpRaw).toLocaleString();
    const mpCap = Math.floor(province.population * MANPOWER_CAP_FRACTION_FOR_HEADER);
    title.textContent = `Province ${province.id} — ${province.tier}, ${Math.round(province.population).toLocaleString()} pop, ${mpDisplay}/${mpCap.toLocaleString()} mp`;

    // Build tab affordability.
    // MFix16.2 (2026-07-29) — capital scope only. Structures/queue entries
    // attributed to a minor (`settlementId !== undefined`) live under the
    // minor's own cap in minor-settlement-panel; they must NOT be counted
    // against the capital's tier cap. The sim already partitions by
    // settlementId in apply-commands.ts build_structure — this client
    // header was double-counting minors' structures against the capital.
    const slotCap = STRUCTURE_CAP_BY_TIER[province.tier];
    let builtCapital = 0;
    for (const s of province.structures) if (s.settlementId === undefined) builtCapital++;
    let queuedCapital = 0;
    for (const q of province.buildQueue) if (q.settlementId === undefined) queuedCapital++;
    const currentSlots = builtCapital + queuedCapital;
    if (buildCapNote) {
      // MFix8.3 (2026-07-24) — always show current/cap. Red when at cap.
      const suffix = queuedCapital > 0 ? ` (+${queuedCapital} in queue)` : '';
      buildCapNote.textContent = `Structures: ${builtCapital} / ${slotCap}${suffix}`;
      buildCapNote.style.color = currentSlots >= slotCap ? '#c47474' : '#f5eacf';
      buildCapNote.style.display = '';
    }
    // v0M9.12 (2026-07-24) — snapshot researched knowledge once per
    // frame; used by the Unlocks Structure gate below.
    const researched = new Set(faction.researchedKnowledge ?? []);

    for (const row of buildRows) {
      const spec = STRUCTURES[row.name];
      if (!spec) continue;
      const affordable = faction.resources.gold >= spec.goldCost;
      const roomLeft = currentSlots < slotCap;
      // MFix16.2 (2026-07-29) — count capital-scope only (settlementId === undefined).
      // A structure of the same name built at a minor should not lock the
      // capital's row to "Built"; queued at a minor should not show "Queued".
      const builtCount = province.structures.reduce(
        (n, s) => n + (s.settlementId === undefined && s.name === row.name ? 1 : 0),
        0,
      );
      const queued = province.buildQueue.some(
        (q) => q.settlementId === undefined && q.name === row.name,
      );
      // v0M9.12 — research gate. If a knowledge unlocks this structure
      // and it hasn't been researched, the row locks with a tooltip.
      const requiredKnowledge = STRUCTURE_UNLOCK_KNOWLEDGE.get(row.name);
      const researchGateMet = !requiredKnowledge || researched.has(requiredKnowledge);
      // MFix10 — count badge. Bright when built, dim when 0.
      row.countBadge.textContent = `${builtCount}×`;
      row.countBadge.style.opacity = builtCount > 0 ? '0.95' : '0.5';
      row.countBadge.style.color = builtCount > 0 ? '#f4d47a' : '';
      if (!researchGateMet) {
        // Research-locked. Show which knowledge is needed in the tooltip.
        row.button.textContent = 'Locked';
        row.button.disabled = true;
        row.button.title = `Requires: ${requiredKnowledge}`;
      } else if (builtCount > 0) {
        // At v1 the sim only permits one copy of each structure per
        // settlement, so button locks to "Built" as soon as one exists.
        // If the sim later allows duplicates, remove this branch and let
        // the affordability + cap gates decide.
        row.button.textContent = 'Built';
        row.button.disabled = true;
        row.button.title = '';
      } else if (queued) {
        row.button.textContent = 'Queued';
        row.button.disabled = true;
        row.button.title = '';
      } else {
        row.button.textContent = 'Build';
        row.button.disabled = !affordable || !roomLeft;
        row.button.title = '';
      }
    }

    // MFix10 (2026-07-24) — build-queue viz. Rebuild rows only when queue
    // shape changes (length + structure-name sequence); otherwise mutate
    // fill widths in place per frame so the bar animates smoothly.
    if (buildQueueList && buildQueueHeader) {
      const queue = province.buildQueue;
      // MFix16.2 (2026-07-29) — capital-scope orders only. `qi` must remain
      // the index into the ACTUAL queue so cancel_build_structure targets
      // the right entry; the hash + iteration filter to settlementId===undefined.
      const capitalIndices: number[] = [];
      for (let i = 0; i < queue.length; i++) {
        if (queue[i]!.settlementId === undefined) capitalIndices.push(i);
      }
      const structureHash = capitalIndices.map((i) => queue[i]!.name).join('|');
      if (structureHash !== lastBuildQueueStructureHash) {
        buildQueueList.innerHTML = '';
        buildQueueRows = [];
        for (const qi of capitalIndices) {
          const order = queue[qi]!;
          const spec = STRUCTURES[order.name];
          if (!spec) continue;
          const rowDiv = document.createElement('div');
          rowDiv.style.margin = '4px 0';
          rowDiv.style.display = 'flex';
          rowDiv.style.alignItems = 'center';
          rowDiv.style.gap = '6px';
          const contentDiv = document.createElement('div');
          contentDiv.style.flex = '1';
          const labelDiv = document.createElement('div');
          labelDiv.style.fontSize = '11px';
          labelDiv.style.opacity = '0.85';
          labelDiv.style.marginBottom = '2px';
          contentDiv.appendChild(labelDiv);
          const barOuter = document.createElement('div');
          barOuter.style.height = '6px';
          barOuter.style.background = 'rgba(255, 255, 255, 0.12)';
          barOuter.style.borderRadius = '3px';
          barOuter.style.overflow = 'hidden';
          const barFill = document.createElement('div');
          barFill.style.height = '100%';
          barFill.style.background = '#f4d47a';
          barFill.style.width = '0%';
          barFill.style.transition = 'width 200ms linear';
          barOuter.appendChild(barFill);
          contentDiv.appendChild(barOuter);
          rowDiv.appendChild(contentDiv);
          // MFix11.6 (2026-07-27) — cancel-X. Dispatches
          // cancel_build_structure with the queue index captured at
          // row construction. Queue rebuilds happen on head-completion
          // and structural changes; between rebuilds indices are
          // stable, so captured `qi` is correct at click time.
          const cancelBtn = document.createElement('button');
          cancelBtn.textContent = '✕';
          cancelBtn.title = 'Cancel this build (no refund)';
          cancelBtn.style.width = '18px';
          cancelBtn.style.height = '18px';
          cancelBtn.style.padding = '0';
          cancelBtn.style.background = '#4a3020';
          cancelBtn.style.color = '#f5eacf';
          cancelBtn.style.border = '1px solid #6a5238';
          cancelBtn.style.borderRadius = '3px';
          cancelBtn.style.cursor = 'pointer';
          cancelBtn.style.fontFamily = 'inherit';
          cancelBtn.style.fontSize = '10px';
          cancelBtn.style.lineHeight = '1';
          cancelBtn.addEventListener('click', () => {
            if (playerFactionId === null) return;
            callbacks.onIssue({
              type: 'cancel_build_structure',
              tick: 0,
              playerFactionId,
              provinceId: province.id,
              index: qi,
            });
          });
          rowDiv.appendChild(cancelBtn);
          buildQueueList.appendChild(rowDiv);
          buildQueueRows.push({
            container: rowDiv,
            label: labelDiv,
            progressFill: barFill,
            structureName: order.name,
            buildTimeSec: spec.buildTimeSec,
          });
        }
        lastBuildQueueStructureHash = structureHash;
      }
      buildQueueHeader.style.display = capitalIndices.length > 0 ? '' : 'none';
      // MFix8.2 concurrency cap = 1 by default: only order 0 advances per
      // tick; the rest show "Queued (N ahead)" instead of a live timer.
      // MFix16.2 (2026-07-29) — iterate capitalIndices (not the raw queue)
      // so rows and their orders align 1:1 in capital-scope space.
      for (let i = 0; i < buildQueueRows.length && i < capitalIndices.length; i++) {
        const row = buildQueueRows[i]!;
        const order = queue[capitalIndices[i]!]!;
        const total = row.buildTimeSec > 0 ? row.buildTimeSec : 1;
        const pct = Math.max(0, Math.min(100, (order.progressSec / total) * 100));
        row.progressFill.style.width = `${pct.toFixed(1)}%`;
        const remaining = Math.max(0, total - order.progressSec);
        if (i === 0) {
          row.label.textContent = `${row.structureName} — ${remaining.toFixed(0)}s remaining`;
        } else {
          row.label.textContent = `${row.structureName} — Queued (${i} ahead)`;
        }
      }
    }

    // Recruit tab.
    if (recruitVillageNote) {
      recruitVillageNote.style.display = province.tier === 'Village' ? '' : 'none';
    }

    // v0M4.2.1 — Garrison section refresh. Reads the city entity that
    // lives in this province (if any) and its current garrison count +
    // in-flight garrison recruits. Button enabled iff:
    //   - a Settlement exists in this province (at v1 = capital only; minor
    //     cities aren't yet materialized in state.settlements)
    //   - garrison count < cap
    //   - faction can afford the Garrison recruit cost
    //   - province has enough population
    // Garrison recruit skips the Village tier gate — every city can
    // garrison-recruit per BATTLES.md §3.2.
    if (garrisonSection && garrisonCountLabel && garrisonRecruitBtn && garrisonNoCityNote) {
      const city = findCityInProvince(state, province.id);
      // v0M9.13 (2026-07-24) — effective cap = base + Garrison Quarters
      // bonuses. Recomputed per frame so newly-completed structures
      // surface immediately.
      const effectiveGarrisonCap = GARRISON_MAX_PER_SETTLEMENT + garrisonMaxBonus(province, STRUCTURES);
      if (!city) {
        garrisonCountLabel.textContent = `— / ${effectiveGarrisonCap}`;
        garrisonRecruitBtn.textContent = 'Recruit';
        garrisonRecruitBtn.disabled = true;
        garrisonNoCityNote.style.display = '';
      } else {
        // Count = existing garrisons in this city + queued garrison
        // recruits targeting this city across every province.
        let queuedForCity = 0;
        for (const p of state.provinces.values()) {
          for (const o of p.recruitQueue) {
            if (o.settlementId === city.id) queuedForCity++;
          }
        }
        const total = city.garrisonCohortIds.length + queuedForCity;
        garrisonCountLabel.textContent = `${city.garrisonCohortIds.length} / ${effectiveGarrisonCap}` +
          (queuedForCity > 0 ? ` (+${queuedForCity} in queue)` : '');
        const garrisonSpec = COHORTS[GARRISON_COHORT_TYPE];
        const atCap = total >= effectiveGarrisonCap;
        // MFix6.6.6 (2026-07-23): garrison affordability now checks
        // this city's manpower against garrison figureCount (1 mp per
        // figure) instead of province population.
        const cityManpower = city.manpower ?? 0;
        const affordable = garrisonSpec !== undefined
          && faction.resources.gold >= garrisonSpec.goldCost
          && faction.resources.iron >= garrisonSpec.ironCost
          && faction.resources.wood >= garrisonSpec.woodCost
          && faction.resources.horses >= garrisonSpec.horsesCost
          && faction.resources.supplies >= garrisonSpec.suppliesCost
          && cityManpower >= garrisonSpec.figureCount;
        if (atCap) {
          garrisonRecruitBtn.textContent = 'At Cap';
          garrisonRecruitBtn.disabled = true;
        } else {
          garrisonRecruitBtn.textContent = 'Recruit';
          garrisonRecruitBtn.disabled = !affordable;
        }
        garrisonNoCityNote.style.display = 'none';
      }
    }

    // MFix1.3 — recruitment queue visualization. Rebuild rows only when
    // the queue structure (length + cohort-type sequence) changes;
    // otherwise mutate the progress-fill width in place so the bar
    // animates smoothly without clobbering DOM every frame.
    if (recruitQueueList && recruitQueueHeader) {
      const queue = province.recruitQueue;
      // v0M5.4d (2026-07-25) — hash now includes specialUnitType so that
      // a queued Spy triggers a rebuild (its cohortType is '').
      const structureHash = queue.map((o) => `${o.cohortType}:${o.specialUnitType ?? ''}`).join('|');
      if (structureHash !== lastRecruitQueueStructureHash) {
        recruitQueueList.innerHTML = '';
        recruitQueueRows = [];
        for (let qi = 0; qi < queue.length; qi++) {
          const order = queue[qi]!;
          // v0M5.4d — resolve display name + duration by branch. Special-
          // unit orders derive duration from SpecialUnits xlsx
          // (recruitTimeDays × TICKS_PER_GAME_DAY × SECONDS_PER_TICK,
          // matching the sim-side threshold in economy.ts stepRecruitQueue).
          let displayName: string;
          let recruitTimeSec: number;
          let isSpecialUnit: boolean;
          if (order.specialUnitType !== undefined && order.specialUnitType !== '') {
            const spec = SPECIAL_UNITS[order.specialUnitType];
            if (!spec) continue;
            displayName = order.specialUnitType;
            recruitTimeSec = spec.recruitTimeDays * TICKS_PER_GAME_DAY * SECONDS_PER_TICK;
            isSpecialUnit = true;
          } else {
            const spec = COHORTS[order.cohortType];
            if (!spec) continue;
            displayName = order.cohortType;
            recruitTimeSec = spec.recruitTimeSec;
            isSpecialUnit = false;
          }
          const rowDiv = document.createElement('div');
          rowDiv.style.margin = '4px 0';
          rowDiv.style.display = 'flex';
          rowDiv.style.alignItems = 'center';
          rowDiv.style.gap = '6px';
          const contentDiv = document.createElement('div');
          contentDiv.style.flex = '1';
          const labelDiv = document.createElement('div');
          labelDiv.style.fontSize = '11px';
          labelDiv.style.opacity = '0.85';
          labelDiv.style.marginBottom = '2px';
          contentDiv.appendChild(labelDiv);
          const barOuter = document.createElement('div');
          barOuter.style.height = '6px';
          barOuter.style.background = 'rgba(255, 255, 255, 0.12)';
          barOuter.style.borderRadius = '3px';
          barOuter.style.overflow = 'hidden';
          const barFill = document.createElement('div');
          barFill.style.height = '100%';
          barFill.style.background = '#f4d47a';
          barFill.style.width = '0%';
          barFill.style.transition = 'width 200ms linear';
          barOuter.appendChild(barFill);
          contentDiv.appendChild(barOuter);
          rowDiv.appendChild(contentDiv);
          // MFix11.6 (2026-07-27) — cancel-X. Dispatches
          // cancel_recruit_cohort with the queue index captured at
          // construction. See build-queue rebuild note above for the
          // index-stability rationale.
          const cancelBtn = document.createElement('button');
          cancelBtn.textContent = '✕';
          cancelBtn.title = 'Cancel this recruit (no refund)';
          cancelBtn.style.width = '18px';
          cancelBtn.style.height = '18px';
          cancelBtn.style.padding = '0';
          cancelBtn.style.background = '#4a3020';
          cancelBtn.style.color = '#f5eacf';
          cancelBtn.style.border = '1px solid #6a5238';
          cancelBtn.style.borderRadius = '3px';
          cancelBtn.style.cursor = 'pointer';
          cancelBtn.style.fontFamily = 'inherit';
          cancelBtn.style.fontSize = '10px';
          cancelBtn.style.lineHeight = '1';
          cancelBtn.addEventListener('click', () => {
            if (playerFactionId === null) return;
            callbacks.onIssue({
              type: 'cancel_recruit_cohort',
              tick: 0,
              playerFactionId,
              provinceId: province.id,
              index: qi,
            });
          });
          rowDiv.appendChild(cancelBtn);
          recruitQueueList.appendChild(rowDiv);
          recruitQueueRows.push({
            container: rowDiv,
            label: labelDiv,
            progressFill: barFill,
            displayName,
            recruitTimeSec,
            isSpecialUnit,
          });
        }
        lastRecruitQueueStructureHash = structureHash;
      }
      recruitQueueHeader.style.display = queue.length > 0 ? '' : 'none';
      // Update progress + label text every frame.
      // MFix8.1 (2026-07-24) — orders past index 0 (or > cap-1) show
      // "Queued (N ahead)" instead of a running timer. Only the leading
      // order advances per tick, so the timer only makes sense for the
      // active one.
      for (let i = 0; i < recruitQueueRows.length && i < queue.length; i++) {
        const row = recruitQueueRows[i]!;
        const order = queue[i]!;
        const total = row.recruitTimeSec > 0 ? row.recruitTimeSec : 1;
        const pct = Math.max(0, Math.min(100, (order.progressSec / total) * 100));
        row.progressFill.style.width = `${pct.toFixed(1)}%`;
        const remaining = Math.max(0, total - order.progressSec);
        // v0M5.4d — special-unit targets are always "this city" (spies
        // spawn at the recruiting settlement's cell); cohort targets
        // remain "new army" or "Army #N".
        const targetLabel = row.isSpecialUnit
          ? 'this city'
          : (order.armyId === null ? 'new army' : `Army #${order.armyId as unknown as number}`);
        if (i === 0) {
          row.label.textContent = `${row.displayName} → ${targetLabel} — ${remaining.toFixed(0)}s remaining`;
        } else {
          row.label.textContent = `${row.displayName} → ${targetLabel} — Queued (${i} ahead)`;
        }
      }
    }
    // Refresh army-select options ONLY when the underlying army list
    // actually changes. Rebuilding every frame would clobber user
    // selections mid-click. Hash includes id + cohortCount so we still
    // update the label ("Army #1 (3 cohorts)" → "(4 cohorts)") on
    // recruit completion.
    if (armySelect) {
      const listSnapshot: string[] = [];
      for (const armyId of sortedIds(state.armies)) {
        const army = state.armies.get(armyId)!;
        if (army.ownerFactionId !== playerFactionId) continue;
        listSnapshot.push(`${armyId as unknown as number}:${army.cohortIds.length}`);
      }
      const hash = listSnapshot.join(',');
      if (hash !== lastArmyListHash) {
        const prevValue = armySelect.value;
        armySelect.innerHTML = '';
        const noneOpt = document.createElement('option');
        noneOpt.value = '';
        noneOpt.textContent = 'New army';
        armySelect.appendChild(noneOpt);
        for (const entry of listSnapshot) {
          const [idStr, countStr] = entry.split(':');
          const opt = document.createElement('option');
          opt.value = idStr!;
          opt.textContent = `Army #${idStr} (${countStr} cohorts)`;
          armySelect.appendChild(opt);
        }
        armySelect.value = prevValue;
        lastArmyListHash = hash;
      }
    }
    // MFix6.6.6 (2026-07-23): recruit affordability now reads manpower
    // from the recruiting city (v1 = the single major city in this
    // province). Look it up once per frame rather than per row.
    const recruitCity = findCityInProvince(state, province.id);
    const armyRecruitManpower = recruitCity?.manpower ?? 0;
    for (const row of recruitRows) {
      const spec = row.spec;
      const canAfford =
        faction.resources.gold >= spec.goldCost &&
        faction.resources.iron >= spec.ironCost &&
        faction.resources.wood >= spec.woodCost &&
        faction.resources.horses >= spec.horsesCost &&
        faction.resources.supplies >= spec.suppliesCost;
      const enoughManpower = armyRecruitManpower >= spec.figureCount;
      const prereqsMet = spec.structurePrereq.every((p) =>
        province.structures.some((s) => s.name === p),
      );
      const villageBlocked = province.tier === 'Village';
      const disabled = villageBlocked || !(canAfford && enoughManpower && prereqsMet);
      row.button.disabled = disabled;
      row.button.title = !prereqsMet ? `Requires: ${spec.structurePrereq.join(', ')}` : '';
    }

    // v0M5.3 (2026-07-25) — Special-unit recruit-button gate. Same
    // pattern as cohort buttons but the prereq is a single structure
    // (not comma-list) + gold-only cost (no manpower / other resources
    // at v1 Spy row). Learning prereq blank at v1 → skipped.
    for (const specialBtn of specialRecruitButtons) {
      const specialName = specialBtn.dataset.specialUnitName;
      if (!specialName) continue;
      const spec = SPECIAL_UNITS[specialName];
      if (!spec) { specialBtn.disabled = true; continue; }
      const goldOk = faction.resources.gold >= spec.goldCost;
      const structureOk =
        spec.structurePrereq === '' ||
        province.structures.some((s) => s.name === spec.structurePrereq);
      const learningOk =
        spec.learningPrereq === '' ||
        (faction.researchedKnowledge ?? []).includes(spec.learningPrereq);
      // v0M5.4d (2026-07-25) — while a special-unit recruit of this
      // type is queued anywhere in this faction, disable the button.
      // Otherwise it's trivially easy to burn gold on duplicate spies
      // during the 20-day training window. Player still sees the
      // in-progress row above.
      let alreadyQueued = false;
      for (const p of state.provinces.values()) {
        if (p.ownerFactionId !== faction.id) continue;
        if (p.recruitQueue.some((o) => o.specialUnitType === specialName)) {
          alreadyQueued = true;
          break;
        }
      }
      specialBtn.disabled = alreadyQueued || !(goldOk && structureOk && learningOk);
      const reasons: string[] = [];
      if (alreadyQueued) reasons.push(`${specialName} already in queue`);
      if (!structureOk) reasons.push(`needs ${spec.structurePrereq}`);
      if (!learningOk) reasons.push(`needs knowledge ${spec.learningPrereq}`);
      if (!goldOk) reasons.push(`needs ${spec.goldCost} gold`);
      specialBtn.title = reasons.join(' · ');
      specialBtn.textContent = alreadyQueued ? 'In Queue' : 'Recruit';
    }

    // Unused var — armyId type import placeholder.
    void ({} as { armyId?: ArmyId });
  }

  function sync(state: GameState, openProvinceId: ProvinceId | null, playerFactionId: FactionId | null): void {
    if (openProvinceId === null || playerFactionId === null) {
      backdrop.style.display = 'none';
      renderedProvinceId = null;
      return;
    }
    const province = state.provinces.get(openProvinceId);
    if (!province) {
      backdrop.style.display = 'none';
      renderedProvinceId = null;
      return;
    }
    const provinceKey = openProvinceId as unknown as number;
    if (provinceKey !== renderedProvinceId) {
      structuralBuild(state, province, playerFactionId);
      renderedProvinceId = provinceKey;
      lastArmyListHash = ''; // force armySelect rebuild after structuralBuild
    }
    refresh(state, province, playerFactionId);
    backdrop.style.display = 'flex';
  }

  function destroy(): void {
    backdrop.remove();
  }

  return { sync, destroy };
}
